import Seo from "@/components/Seo";
import { Ornament } from "@/components/Ornament";
import { Reveal } from "@/components/Reveal";
import { MILESTONES, MUSICIANS } from "@/data/site";

export default function About() {
  return (
    <main className="pt-32">
      <Seo
        title="About the Band — 1001 Nights Orchestra"
        siteName="1001 Nights Orchestra"
        description="Founded in Austin in 1987 by oud and santour master Kamran Hooshmand, 1001 Nights Orchestra is Austin's longest-running Middle Eastern ensemble. Meet the musicians."
      />
      <header className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 pb-16">
        <Reveal>
          <p className="font-mono text-xs uppercase tracking-[0.35em] text-amber-400">Since 1987</p>
          <h1 className="mt-4 font-serif text-4xl sm:text-5xl lg:text-6xl text-amber-50 tracking-tight max-w-3xl">
            Austin's longest-running <span className="italic gold-text">Middle Eastern ensemble</span>
          </h1>
        </Reveal>
      </header>

      <section data-testid="musicians-section" className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 pb-24">
        <Reveal>
          <p className="font-mono text-xs uppercase tracking-[0.35em] text-amber-400">The Ensemble</p>
          <h2 className="mt-4 font-serif text-3xl sm:text-4xl text-amber-50 tracking-tight mb-12">The Players</h2>
        </Reveal>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {MUSICIANS.map((m, i) => (
            <Reveal key={m.name} delay={(i % 3) * 0.08}>
              <article
                data-testid={`musician-card-${i}`}
                className="group border border-amber-500/15 bg-surface/60 overflow-hidden hover:border-amber-400/40 transition-all duration-300 hover:-translate-y-1"
              >
                <div className="overflow-hidden">
                  {m.image ? (
                    <img
                      src={m.image}
                      alt={`${m.name} — ${m.role}`}
                      loading="lazy"
                      className="w-full aspect-[4/5] object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                  ) : (
                    <div
                      data-testid={`musician-monogram-${i}`}
                      aria-label={`${m.name} — portrait coming soon`}
                      className="relative w-full aspect-[4/5] flex items-center justify-center bg-midnight"
                      style={{ background: "radial-gradient(ellipse at 50% 35%, rgba(212,175,55,0.14), rgba(7,10,18,1) 70%)" }}
                    >
                      <span className="absolute inset-4 border border-amber-500/15" />
                      <span className="font-serif text-7xl gold-text italic tracking-tight">
                        {m.name.split(" ").map((w) => w[0]).join("")}
                      </span>
                      <span className="absolute bottom-7 font-mono text-[10px] uppercase tracking-[0.3em] text-slate-500">Portrait coming soon</span>
                    </div>
                  )}
                </div>
                <div className="p-6">
                  <h3 className="font-serif text-2xl text-amber-100">{m.name}</h3>
                  <p className="mt-1 font-mono text-[11px] uppercase tracking-[0.2em] text-amber-400">{m.role}</p>
                  <p className="mt-3 text-sm text-slate-400 leading-relaxed">{m.bio}</p>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
        <Ornament className="mt-20" />
        <p className="mt-8 text-center text-sm text-slate-500 max-w-2xl mx-auto leading-relaxed">
          The orchestra changes shape over time — past members and guest collaborators
          have brought violin, qanun, oboe, frame drums, didgeridoo and even hurdy-gurdy to the stage. Founder{" "}
          <span className="text-amber-300/80">Kamran Hooshmand</span>, who led the orchestra from
          1987 until his passing in 2016, remains its guiding spirit.
        </p>
      </section>

      <section data-testid="story-section" className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 grid grid-cols-1 lg:grid-cols-12 gap-12 pt-20 pb-24 border-t border-amber-500/10">
        <Reveal className="lg:col-span-5">
          <aside
            data-testid="founder-tribute-panel"
            className="relative border border-amber-500/20 bg-surface/60 p-6 sm:p-8 overflow-hidden"
          >
            <span className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(ellipse at 50% 0%, rgba(212,175,55,0.10), transparent 60%)" }} />
            <p className="relative font-mono text-[10px] uppercase tracking-[0.35em] text-amber-400">In Memoriam</p>
            <figure
              data-testid="band-photo-frame"
              className="relative mt-6 inline-block border border-amber-500/25 bg-midnight p-3 shadow-[0_20px_40px_-15px_rgba(0,0,0,0.7)] -rotate-1 hover:rotate-0 transition-transform duration-500"
            >
              <img
                src="/photos/kamran.jpg"
                alt="Kamran Hooshmand, founder of 1001 Nights Orchestra, playing the oud"
                className="w-full max-w-sm"
              />
              <figcaption className="pt-3 pb-1 text-center font-mono text-[10px] uppercase tracking-[0.25em] text-amber-400/80">
                Kamran Hooshmand {"\u25C8"} Oud
              </figcaption>
            </figure>
            <h2 className="relative mt-8 font-serif text-2xl sm:text-3xl text-amber-50 tracking-tight">
              Kamran Hooshmand
            </h2>
            <p className="relative mt-1 font-mono text-[11px] uppercase tracking-[0.2em] text-amber-400">
              Founder {"\u25C8"} Oud · Santour · Rabab · Saz {"\u25C8"} 1987–2016
            </p>
            <p className="relative mt-4 text-sm text-slate-400 leading-relaxed">
              Iranian-born multi-instrumentalist and musicologist, Kamran gathered a circle of
              Austin musicians in 1987 and gave them a name and a repertoire that still fills
              rooms today. Every melody the orchestra plays carries his ear for the music of the
              Middle East, Latin America and the Balkans — and his belief that it belonged on
              every stage.
            </p>
            <Ornament className="relative mt-6" />
          </aside>
        </Reveal>
        <Reveal delay={0.15} className="lg:col-span-7 space-y-6 text-slate-300 leading-relaxed">
          <p>
            It began in 1987 at Chicago House, an Austin venue where a circle of musicians
            gathered to play, experiment and improvise together. Before long, those sessions
            became <span className="text-amber-200">1001 Nights Orchestra</span>. What started
            with Persian folk and popular music grew into something far wider — a living
            repertoire shaped by the traditions of the Middle East, Latin America and the Balkans.
          </p>
          <p>
            Founded and led for nearly three decades by Iranian-born multi-instrumentalist{" "}
            <span className="text-amber-200">Kamran Hooshmand</span> — oud, santour, rabab, saz,
            guitar and voice — until his passing in 2016, the ensemble he gathered carries his
            archive forward through many lineups, each musician leaving an imprint on the sound.
          </p>
          <p>
            Along the way the orchestra recorded several albums, launched its sister project{" "}
            <span className="italic text-amber-100">Ojalá</span> — Middle Eastern repertoire in
            conversation with Latin American melodies and rhythms — and composed an award-winning
            live score to the 1924 silent classic{" "}
            <span className="italic text-amber-100">The Thief of Bagdad</span>. Beyond the stage,
            it helped create Austin's annual Nowruz celebrations at Central Market, gathering
            music, culture and community around the Persian New Year.
          </p>
          <div className="pt-4 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-6">
            {MILESTONES.map((m) => (
              <div key={m.year} className="border-l-2 border-amber-500/30 pl-4">
                <span className="font-mono text-sm text-amber-300">{m.year}</span>
                <p className="mt-1 text-sm text-slate-400">{m.text}</p>
              </div>
            ))}
          </div>
        </Reveal>
      </section>

    </main>
  );
}
